import { Play, Pause, RefreshCw, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { SceneVideoThumbnail } from './SceneVideoThumbnail';
import { SceneCardHeader } from './PropertySceneCardHeader';
import { SceneThumbnail } from './PropertySceneCardShared';
import { PromptTemplateSelectMemo } from './PromptTemplateSelect';
import type { IPropertySceneCardProps } from '@/@types/propertySceneEditor';
import type { IPromptTemplateBasic } from '@/@types/propertySceneCard';
import type { Id } from '../../../convex/_generated/dataModel';
import type { PromptTemplateImageType } from '@/@types/promptTemplates';

function getEffectiveSourceImage(
  scene: IPropertySceneCardProps['scene']
): Id<'_storage'> | undefined {
  return scene.selectedImageId ?? scene.sourceImageId;
}

function getEffectiveVoiceoverText(scene: IPropertySceneCardProps['scene']): string {
  return scene.editedVoiceoverText ?? scene.voiceoverText;
}

function getEffectiveImagePrompt(
  scene: IPropertySceneCardProps['scene'],
  template: IPromptTemplateBasic | null | undefined
): string {
  if (scene.editedImagePrompt) return scene.editedImagePrompt;
  if (template) return template.positivePrompt;
  return scene.imageToVideoPrompt ?? '';
}

interface IHorizontalLayoutProps extends IPropertySceneCardProps {
  isPlayingAudio: boolean;
  onPlayPause: () => void;
}

export function HorizontalLayout({
  scene,
  dragHandleProps,
  aspectRatio,
  isSaving,
  isRegeneratingVoiceover,
  isRegeneratingVideo,
  isDeleting,
  isPlayingAudio,
  onPlayPause,
  onImageSelect,
  onPromptEdit,
  onRegenerateVoiceover,
  onRegenerateVideo,
  onExpandVideo,
  onDelete,
  onTemplateChange,
}: IHorizontalLayoutProps) {
  const effectiveImageId = getEffectiveSourceImage(scene);

  const template = useQuery(
    api.property.promptTemplates.getUnified,
    scene.promptTemplateId ? { templateId: scene.promptTemplateId } : 'skip'
  ) as IPromptTemplateBasic | null | undefined;

  const effectivePositivePrompt = getEffectiveImagePrompt(scene, template);

  const actionButtons = (
    <>
      {scene.voiceoverAudioId && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onPlayPause}
          aria-label={isPlayingAudio ? 'Pause voiceover' : 'Play voiceover'}
        >
          {isPlayingAudio ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={onRegenerateVoiceover}
        disabled={isRegeneratingVoiceover}
        aria-label={scene.voiceoverAudioId ? 'Regenerate voiceover' : 'Generate voiceover'}
      >
        <RefreshCw className={cn('h-4 w-4', isRegeneratingVoiceover && 'animate-spin')} />
      </Button>
    </>
  );

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      <SceneCardHeader
        sceneNumber={scene.sceneNumber}
        sceneType={scene.sceneType}
        isDeleting={isDeleting}
        onDelete={onDelete}
        dragHandleProps={dragHandleProps}
        compact
        actionButtons={actionButtons}
      />

      <div className="flex items-stretch gap-3 p-3">
        <div className="flex gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Source Image</span>
            <SceneThumbnail
              storageId={effectiveImageId}
              sceneType={scene.sceneType}
              onChangeClick={onImageSelect}
              size="small"
              aspectRatio={aspectRatio}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Generated Video</span>
            <SceneVideoThumbnail
              videoStorageId={scene.generatedVideoId}
              status={scene.imageToVideoStatus || 'pending'}
              aspectRatio={aspectRatio}
              size="small"
              hasExistingVideo={!!scene.generatedVideoId}
              isRegenerating={isRegeneratingVideo}
              onPlay={() => {}}
              onExpand={onExpandVideo}
              onRegenerate={onRegenerateVideo}
            />
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm text-muted-foreground">
            &quot;{getEffectiveVoiceoverText(scene)}&quot;
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Style:</span>
            <div className="w-40">
              <PromptTemplateSelectMemo
                value={scene.promptTemplateId ?? null}
                onChange={templateId => onTemplateChange?.(scene._id, templateId)}
                imageType={scene.sceneType as PromptTemplateImageType}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="pt-1 border-t">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground shrink-0">
                I2V Prompt:
              </span>
              <p className="text-xs text-foreground truncate flex-1 min-w-0">
                {effectivePositivePrompt || (
                  <span className="text-muted-foreground italic">No prompt set</span>
                )}
              </p>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={onPromptEdit}
                aria-label="Edit video prompts"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
